/**
 * Buy Together (Compre Junto) custom element.
 *
 * – Reads embedded product JSON from each card.
 * – Manages variant selection via <select> elements.
 * – Filters available options to prevent invalid combinations.
 * – Handles checkbox toggling per card.
 * – Batch-adds all selected variants to cart via /cart/add.js.
 * – Dispatches cart:update on success (compatible with Horizon cart drawer).
 *
 * The CTA button uses class `rk-buy-together__button` with modifier classes
 * (`--disabled`, `--loading`, `--success`).
 */
class RkBuyTogether extends HTMLElement {
  /**
   * Maps card HTMLElement → { json, variantId, selected, isMain }
   * @type {Map<HTMLElement, { json: object, variantId: number|null, selected: boolean, isMain: boolean }>}
   */
  #cards = new Map();

  /** @type {HTMLButtonElement | null} */
  #button = null;

  /* ── Bound handler references (for removeEventListener) ─── */
  #boundHandleChange = this.#handleSelectChange.bind(this);
  #boundHandleClick = this.#handleClick.bind(this);

  /* ── Lifecycle ─────────────────────────────────────────── */

  connectedCallback() {
    this.#button = this.querySelector('[data-buy-together-cta]');

    for (const card of this.querySelectorAll('.rk-buy-together__card')) {
      const jsonEl = card.querySelector('.rk-buy-together__product-json');
      if (!jsonEl) continue;

      let json;
      try {
        json = JSON.parse(jsonEl.textContent ?? '{}');
      } catch {
        console.error('[rk-buy-together] Failed to parse product JSON', jsonEl.textContent);
        continue;
      }

      const isMain = card.hasAttribute('data-main-product');
      const state = { json, variantId: null, selected: false, isMain };
      this.#cards.set(/** @type {HTMLElement} */ (card), state);

      // Auto-select single-variant products that are available
      if (json.variants?.length === 1 && json.variants[0].available) {
        const variant = json.variants[0];
        state.variantId = variant.id;
        state.selected = true;
        this.#setCardSelected(/** @type {HTMLElement} */ (card), true);

        // Hide the variants section when the product has only one option (default variant)
        const variantsEl = card.querySelector('.rk-buy-together__variants');
        if (variantsEl && json.options?.length <= 1) {
          variantsEl.style.display = 'none';
        }
      }
    }

    this.addEventListener('change', this.#boundHandleChange);
    this.addEventListener('click', this.#boundHandleClick);

    // Listen for carousel init to track scroll position for left gradient
    this.addEventListener('rk:carousel:init', this.#handleCarouselInit);

    this.#updateButtonState();
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.#boundHandleChange);
    this.removeEventListener('click', this.#boundHandleClick);
    this.removeEventListener('rk:carousel:init', this.#handleCarouselInit);
  }

  /* ── Carousel scroll gradient ─────────────────────────── */

  #handleCarouselInit = (/** @type {CustomEvent} */ e) => {
    const swiper = e.detail?.swiper;
    if (!swiper) return;

    const carousel = /** @type {HTMLElement | null} */ (
      this.querySelector('.rk-carousel')
    );
    if (!carousel) return;

    const updateGradient = () => {
      if (swiper.isBeginning) {
        carousel.classList.remove('rk-carousel--scrolled');
      } else {
        carousel.classList.add('rk-carousel--scrolled');
      }
    };

    swiper.on('slideChange', updateGradient);
    swiper.on('progress', updateGradient);
    updateGradient();
  };

  /* ── Select change ─────────────────────────────────────── */

  /**
   * @param {Event} e
   */
  #handleSelectChange(e) {
    const target = /** @type {HTMLElement} */ (e.target);
    if (!target.classList.contains('rk-buy-together__select')) return;

    const card = /** @type {HTMLElement | null} */ (target.closest('.rk-buy-together__card'));
    if (!card) return;

    const state = this.#cards.get(card);
    if (!state) return;

    const { json } = state;
    const selects = /** @type {NodeListOf<HTMLSelectElement>} */ (
      card.querySelectorAll('.rk-buy-together__select')
    );

    // Collect currently selected option values (empty string = not selected)
    const selectedOptions = Array.from(selects).map((s) => s.value);

    // Update available options based on current selections
    this.#filterOptions(card, json, selects, selectedOptions);

    // Re-read selected options after filtering (some may have been reset)
    const currentOptions = Array.from(selects).map((s) => s.value);
    const allSelected = currentOptions.every((v) => v !== '');

    if (!allSelected) {
      state.variantId = null;
      state.selected = false;
      this.#setCardSelected(card, false);
      this.#updateButtonState();
      return;
    }

    // Find matching variant
    const variant = this.#findVariant(json.variants, currentOptions);

    if (variant && variant.available) {
      state.variantId = variant.id;
      state.selected = true;
      this.#setCardSelected(card, true);
    } else {
      state.variantId = null;
      state.selected = false;
      this.#setCardSelected(card, false);
    }

    this.#updateButtonState();
  }

  /* ── Option filtering ──────────────────────────────────── */

  /**
   * For each select, disable options that don't form a valid variant
   * combination with the other currently selected options.
   * Resets a select to empty if its current value becomes disabled.
   *
   * @param {HTMLElement} card
   * @param {object} json
   * @param {NodeListOf<HTMLSelectElement>} selects
   * @param {string[]} selectedOptions
   */
  #filterOptions(card, json, selects, selectedOptions) {
    const { variants } = json;

    selects.forEach((select, selectIdx) => {
      // Build the "other" selected options (all except the current select)
      const otherOptions = selectedOptions.filter((_, i) => i !== selectIdx);

      for (const option of select.options) {
        // Skip the placeholder (empty value)
        if (option.value === '') continue;

        // Check if this option value exists in any variant that matches the
        // other currently selected (non-empty) option values.
        const isValid = variants.some((variant) => {
          const variantOptions = [variant.option1, variant.option2, variant.option3].slice(
            0,
            selects.length
          );

          // This option's position must match
          if (variantOptions[selectIdx] !== option.value) return false;

          // All other selected (non-empty) options must also match
          return otherOptions.every((otherValue, relIdx) => {
            // Map relative index back to absolute index (skip current selectIdx)
            const absIdx = relIdx < selectIdx ? relIdx : relIdx + 1;
            if (otherValue === '') return true; // Not yet selected — don't constrain
            return variantOptions[absIdx] === otherValue;
          });
        });

        option.disabled = !isValid;
      }

      // If the currently selected value is now disabled, reset the select
      if (select.value !== '' && select.options[select.selectedIndex]?.disabled) {
        select.value = '';
      }
    });
  }

  /* ── Variant lookup ────────────────────────────────────── */

  /**
   * Find the variant that matches all option values (in order).
   *
   * @param {Array<{ id: number, available: boolean, option1: string|null, option2: string|null, option3: string|null }>} variants
   * @param {string[]} selectedOptions
   * @returns {{ id: number, available: boolean } | undefined}
   */
  #findVariant(variants, selectedOptions) {
    return variants.find((variant) => {
      const variantOptions = [variant.option1, variant.option2, variant.option3];
      return selectedOptions.every((value, idx) => variantOptions[idx] === value);
    });
  }

  /* ── Click handler ─────────────────────────────────────── */

  /**
   * @param {MouseEvent} e
   */
  #handleClick(e) {
    const target = /** @type {HTMLElement} */ (e.target);

    // Checkbox toggle
    const checkbox = target.closest('.rk-buy-together__checkbox');
    if (checkbox instanceof HTMLButtonElement) {
      if (checkbox.disabled) return;

      const card = /** @type {HTMLElement | null} */ (checkbox.closest('.rk-buy-together__card'));
      if (!card) return;

      const state = this.#cards.get(card);
      if (!state || state.variantId === null) return;

      // Main product is locked — cannot be deselected
      if (state.isMain) return;

      state.selected = !state.selected;
      this.#setCardSelected(card, state.selected);
      this.#updateButtonState();
      return;
    }

    // CTA button
    const btn = target.closest('[data-buy-together-cta]');
    if (btn instanceof HTMLButtonElement) {
      if (btn.disabled || btn.classList.contains('rk-btn--disabled')) return;
      this.#addToCart();
    }
  }

  /* ── Card visual state ─────────────────────────────────── */

  /**
   * @param {HTMLElement} card
   * @param {boolean} selected
   */
  #setCardSelected(card, selected) {
    card.classList.toggle('rk-buy-together__card--selected', selected);

    const checkbox = /** @type {HTMLButtonElement | null} */ (
      card.querySelector('.rk-buy-together__checkbox')
    );
    if (!checkbox) return;

    checkbox.classList.toggle('rk-buy-together__checkbox--active', selected);
    checkbox.setAttribute('aria-pressed', String(selected));

    const state = this.#cards.get(card);
    // Main card stays "enabled-looking" but unclickable (handled in #handleClick)
    if (state?.isMain) {
      checkbox.disabled = false;
      return;
    }
    // Other cards: enable checkbox only when a variant is resolved
    checkbox.disabled = !state || state.variantId === null;
  }

  /* ── Button state ──────────────────────────────────────── */

  #updateButtonState() {
    if (!this.#button) return;

    const states = Array.from(this.#cards.values());
    const mainState = states.find((s) => s.isMain);
    const mainReady = !mainState || (mainState.variantId !== null && mainState.selected);
    const hasOtherSelection = states.some(
      (s) => !s.isMain && s.selected && s.variantId !== null
    );

    const enabled = mainReady && hasOtherSelection;

    this.#button.disabled = !enabled;
    this.#button.classList.toggle('rk-btn--disabled', !enabled);
  }

  /* ── Add to cart ───────────────────────────────────────── */

  async #addToCart() {
    const items = Array.from(this.#cards.values())
      .filter((state) => state.selected && state.variantId !== null)
      .map((state) => ({ id: /** @type {number} */ (state.variantId), quantity: 1 }));

    if (!items.length) return;

    const btn = this.#button;
    if (!btn) return;

    // Loading state — use rk-btn--loading class
    btn.disabled = true;
    btn.classList.add('rk-btn--loading');
    btn.classList.remove('rk-btn--disabled');

    // Inject rk-spinner if not already present
    if (!btn.querySelector('.rk-spinner')) {
      const spinner = document.createElement('span');
      spinner.className = 'rk-spinner rk-spinner--sm';
      spinner.setAttribute('aria-hidden', 'true');
      btn.appendChild(spinner);
    }

    try {
      const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });

      if (!response.ok) throw new Error(`Add to cart failed: ${response.status}`);

      this.#dispatchCartUpdate();

      // Remove loading, restore button
      btn.classList.remove('rk-btn--loading');
      const spinner = btn.querySelector('.rk-spinner');
      if (spinner) spinner.remove();

      this.#updateButtonState();
    } catch (err) {
      console.error('[rk-buy-together]', err);

      btn.classList.remove('rk-btn--loading');
      const spinner = btn.querySelector('.rk-spinner');
      if (spinner) spinner.remove();

      this.#updateButtonState();
    }
  }

  /* ── Cart update event ─────────────────────────────────── */

  #dispatchCartUpdate() {
    document.dispatchEvent(
      new CustomEvent('cart:update', {
        bubbles: true,
        detail: { data: { source: 'rk-buy-together' } },
      })
    );
  }
}

if (!customElements.get('rk-buy-together')) {
  customElements.define('rk-buy-together', RkBuyTogether);
}
